import { captureException } from '@sentry/react';
import { Button, Flex, message, Spin, Tabs } from 'antd';
import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAdminBillingPeriodController_delete } from '@api-client/generated/AdminBillingPeriodController/delete';
import { useAdminBillingPeriodController_updateOneById } from '@api-client/generated/AdminBillingPeriodController/updateOneById';
import { Schemas } from '@api-client/generated/types';
import { IconEditUnderline, IconPlus, IconTrash } from '@assets';
import { Scrollbar } from '@components';
import { PopupDelete } from '@entities';
import { ModalCreditNote } from '@entities/billing/modals/ModalCreditNote/ModalCreditNote';
import { sumTotal } from '@entities/billing/modals/ModalCreditNote/buildCreditNoteItems';
import { useCurrentCompanyId } from '@hooks';

import BillingTabDetails from '../../tabs/BillingTabDetails';
import BillingTabDocument from '../../tabs/BillingTabDocument';
import { buildCreditNoteTabs } from '../../tabs/buildCreditNoteTabs';
import BillingCardSkeleton from '../BillingCardSkeleton';
import * as S from './styled';

enum TabKey {
  Details = 'details',
  PaymentDocument = 'paymentDocument',
}

type InvoiceItem = Schemas.InvoiceItem;

type BillingCardProps = {
  details: Schemas.BillingPeriod | null;
  isLoading: boolean;
  onAdd: VoidFunction;
  onEdit: VoidFunction;
  onEditService: (details: InvoiceItem, index: number) => void;
  onAfterCreateDocument: (id: string) => void;
  onAfterUpdate: VoidFunction;
};

const getDatePeriod = (period: string) => dayjs(period).format('DD.MM.YYYY');

const BillingPeriodCard = (props: BillingCardProps) => {
  const [creditNoteParentId, setCreditNoteParentId] = useState<string | null>();

  const { id: billingId } = useParams();

  const companyId = useCurrentCompanyId();
  const [selectedTabKey, setSelectedTabKey] = useState<TabKey | string>(
    TabKey.Details
  );
  const [isOpenPopupConfirm, setIsOpenPopupConfirm] = useState(false);
  // Target the delete by array index, not an item id: generated items share
  // hardcoded ids ('1' to '4') and admin-added items have no id at all, so
  // filtering by id can delete several rows at once. We also snapshot the item
  // so we can detect (and abort on) the list changing before confirmation.
  const [serviceToDelete, setServiceToDelete] = useState<{
    index: number;
    item: InvoiceItem;
  } | null>(null);

  const { mutate: removeBilling } = useAdminBillingPeriodController_delete();
  const { mutate: updateBilling } =
    useAdminBillingPeriodController_updateOneById();

  useEffect(() => {
    if (billingId) {
      setSelectedTabKey(TabKey.Details);
    }
  }, [billingId]);

  const handleRemoveBilling = () => {
    const id = props.details?.id;

    if (id) {
      removeBilling(
        {
          parameter: {
            companyId: companyId!,
            id,
          },
        },
        {
          onSuccess: () => {
            setIsOpenPopupConfirm(false);
            props.onAfterUpdate?.();
          },
          onError: (error) => {
            setIsOpenPopupConfirm(false);
            message.error('Failed to delete the billing period');
            captureException(error);
          },
        }
      );
    }
  };

  const handleRemoveService = () => {
    const details = props.details;

    // Guard against a race: the period may have become issued (billingDocument
    // appeared via refetch) between opening the confirm popup and confirming.
    if (!details || !serviceToDelete || details.billingDocument) {
      setServiceToDelete(null);
      return;
    }

    const { index, item } = serviceToDelete;

    // Items have no stable id, so verify the row at the captured index still
    // matches the snapshot taken when the popup opened; abort if the list
    // changed underneath us rather than deleting the wrong service.
    if (!isEqual(details.items[index], item)) {
      setServiceToDelete(null);
      message.error('The service list has changed, please try again');
      return;
    }

    updateBilling(
      {
        parameter: {
          companyId: companyId!,
          id: details.id,
        },
        requestBody: {
          startDate: details.startDate,
          endDate: details.endDate,
          items: details.items.filter((_, i) => i !== index),
        },
      },
      {
        onSuccess: () => {
          setServiceToDelete(null);
          props.onAfterUpdate?.();
        },
        onError: (error) => {
          setServiceToDelete(null);
          message.error('Failed to delete the service');
          captureException(error);
        },
      }
    );
  };

  const canManageServices = Boolean(
    props.details && !props.details.billingDocument
  );

  if (props.isLoading) {
    return (
      <S.Container justify="center" align="center">
        <Spin />
      </S.Container>
    );
  }

  if (!props.details) {
    return (
      <S.Container>
        <BillingCardSkeleton />
      </S.Container>
    );
  }

  return (
    <S.Container vertical>
      <PopupDelete
        open={isOpenPopupConfirm}
        title="Are you sure you want to delete the billing?"
        confirmParams={{
          text: 'Yes, delete',
        }}
        onRemove={handleRemoveBilling}
        onCancel={() => setIsOpenPopupConfirm(false)}
      />

      <PopupDelete
        open={serviceToDelete !== null}
        title="Are you sure you want to delete this service?"
        confirmParams={{
          text: 'Yes, delete',
        }}
        onRemove={handleRemoveService}
        onCancel={() => setServiceToDelete(null)}
      />

      <S.Header align="flex-start" justify="space-between" gap={16}>
        <Flex vertical>
          <S.Title align="center" gap={8}>
            {getDatePeriod(props.details.startDate)} -{' '}
            {getDatePeriod(props.details.endDate)}
            <S.ButtonEdit
              type="link"
              icon={<IconEditUnderline />}
              size="large"
              onClick={props.onEdit}
            />
          </S.Title>

          <S.Details>{props.details.items.length} services</S.Details>
        </Flex>

        {canManageServices && (
          <Flex gap={8}>
            <Button
              type="default"
              size="large"
              icon={<IconPlus />}
              onClick={props.onAdd}
              ghost
            >
              Add service
            </Button>

            <Button
              type="primary"
              size="large"
              icon={<IconTrash />}
              onClick={() => setIsOpenPopupConfirm(true)}
              danger
            />
          </Flex>
        )}
      </S.Header>

      <Tabs
        activeKey={selectedTabKey}
        size="large"
        tabBarExtraContent={
          props.details.billingDocument?.documentMetadata && {
            right: (
              <Button
                icon={<IconPlus />}
                type="link"
                onClick={() =>
                  setCreditNoteParentId(
                    props.details?.billingDocument.id || null
                  )
                }
              >
                Add credit note
              </Button>
            ),
          }
        }
        items={[
          {
            key: TabKey.Details,
            label: 'Details',
            children: (
              <Scrollbar height="calc(100vh - 365px)" withoutPadding>
                <BillingTabDetails
                  dataSource={props.details.items}
                  onAdd={props.onAdd}
                  onEdit={props.onEditService}
                  onDelete={(index) =>
                    setServiceToDelete({ index, item: props.details!.items[index] })
                  }
                  canManageServices={canManageServices}
                />
              </Scrollbar>
            ),
          },
          {
            key: TabKey.PaymentDocument,
            label: 'Payment Document',
            children: (
              <Scrollbar height="calc(100vh - 365px)" withoutPadding>
                <BillingTabDocument
                  key={props.details.id}
                  periodId={props.details.id}
                  documentId={props.details.billingDocument?.id}
                  onAfterCreate={props.onAfterCreateDocument}
                />
              </Scrollbar>
            ),
          },
          ...buildCreditNoteTabs(props.details.billingDocument?.children),
        ]}
        onChange={setSelectedTabKey}
      />

      <ModalCreditNote
        isOpen={Boolean(creditNoteParentId)}
        parentId={creditNoteParentId || undefined}
        parentAmount={
          props.details.billingDocument?.documentMetadata?.amount ?? undefined
        }
        parentCurrencyCode={
          props.details.billingDocument?.documentMetadata?.currencyCode ??
          undefined
        }
        // Source indexes must refer to the immutable issued-document snapshot,
        // never to the mutable billing-period rows.
        parentItems={
          props.details.billingDocument?.documentMetadata?.items ?? []
        }
        creditNotes={props.details.billingDocument?.children}
        alreadyCreditedTotal={(
          props.details.billingDocument?.children ?? []
        ).reduce(
          (acc, child) => {
            const itemTotal = sumTotal(child.documentMetadata?.items ?? []);
            return acc + (itemTotal || child.documentMetadata?.amount || 0);
          },
          0
        )}
        onAfterCreate={props.onAfterUpdate}
        onClose={() => {
          setCreditNoteParentId(null);
        }}
      />
    </S.Container>
  );
};

export default BillingPeriodCard;
